import { useState } from 'react';
import { CategoryItem } from '../../contexts/CategoryContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CategoryManagePanelProps {
  categoryList: CategoryItem[];
  onReorder: (newList: CategoryItem[]) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

function SortableCategoryItem({
  category,
  onDelete,
  isDeleting,
}: {
  category: CategoryItem;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id, disabled: isDeleting });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  const canDelete = !category.isDefault;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white border rounded-lg transition-all ${
        isDragging
          ? 'border-emerald-500 shadow-lg bg-emerald-50'
          : 'border-gray-200 hover:border-gray-300'
      } ${isDeleting ? 'opacity-50' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
      >
        <i className="ri-drag-move-2-fill text-lg"></i>
      </button>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {category.title}
        </p>
        {category.isDefault && (
          <span className="text-xs text-emerald-600">기본 카테고리</span>
        )}
        {category.isCustom && (
          <span className="text-xs text-blue-600">커스텀</span>
        )}
      </div>

      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(category.id)}
          disabled={isDeleting}
          className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors cursor-pointer"
          title="카테고리 삭제"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      )}
    </div>
  );
}

export default function CategoryManagePanel({
  categoryList,
  onReorder,
  onDelete,
  onClose,
}: CategoryManagePanelProps) {
  const [items, setItems] = useState<CategoryItem[]>(categoryList);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        onReorder(newItems);
        return newItems;
      });
    }
  };

  const handleDelete = (id: string) => {
    setShowConfirm(id);
  };

  const confirmDelete = () => {
    if (showConfirm) {
      setDeletingId(showConfirm);
      setTimeout(() => {
        onDelete(showConfirm);
        setItems((prev) => prev.filter((c) => c.id !== showConfirm));
        setDeletingId(null);
        setShowConfirm(null);
      }, 300);
    }
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-700">카테고리 관리</p>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
        >
          닫기
        </button>
      </div>
      
      <p className="text-xs text-gray-500 mb-3">
        <i className="ri-information-line mr-1"></i>
        드래그로 순서 변경 · 삭제 시 해당 제품은 "그 외 다른 제품"으로 이동
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {items.map((category) => (
              <SortableCategoryItem
                key={category.id}
                category={category}
                onDelete={handleDelete}
                isDeleting={deletingId === category.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* 삭제 확인 모달 */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-alert-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">카테고리 삭제</h4>
                <p className="text-sm text-gray-500">
                  이 카테고리를 삭제하시겠습니까?
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6 bg-gray-50 p-3 rounded-lg">
              해당 카테고리의 모든 제품은 <strong>"그 외 다른 제품"</strong> 카테고리로 자동 이동됩니다.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(null)}
                className="flex-1 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}